import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useField } from '@unform/core';

import { makeStyles } from '@material-ui/core/styles';
import { MenuItem, TextField } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';

import TextHelper from '../../helpers/TextHelper';

const useStyles = makeStyles((theme) => ({
  skeleton: {
    borderRadius: 4,
    height: 56,
  },
}));

export default function Input({
  name = '',
  type = 'text',
  loading = false,
  mask = '',
  numeric = false,
  options = null,
  maxLength = null,
  inputProps = {},
  InputLabelProps = {},
  ...rest
}) {
  const classes = useStyles();
  const inputRef = useRef(null);

  const getFormattedValue = useCallback(
    (value) => {
      if (mask === 'date' && value?.date) {
        value = value?.date;
      }

      let formatted = TextHelper.toString(value);

      if (numeric || type === 'number') {
        formatted = formatted.replace(/\D/g, '');
      }

      if (mask && type !== 'number') {
        formatted = TextHelper.mask(formatted, mask);
      }

      return formatted;
    },
    [type, mask, numeric],
  );

  const { fieldName, defaultValue, registerField, error, clearError } = useField(name);
  const [manipulated, setManipulated] = useState(getFormattedValue(defaultValue));

  const { readOnly } = rest;
  const select = Boolean(options);
  const isMoney = mask === 'money';

  if (error && typeof error === 'string') {
    rest.helperText = error;
  }

  if (readOnly) {
    inputProps.readOnly = true;
  }

  if (!('maxLength' in inputProps)) {
    inputProps.maxLength = maxLength;
  }

  InputLabelProps.shrink = true;

  const handleChange = (e) => {
    clearError();
    const newValue = getFormattedValue(e.target.value);

    setManipulated(newValue);
    e.target.value = newValue;

    if (typeof rest.onChange === 'function') {
      rest.onChange(e);
    }
  };

  useEffect(() => {
    if (!loading) {
      registerField({
        name: fieldName,
        // ref: inputRef.current,
        ref: inputRef.current?.node || inputRef.current,
        path: 'value',
        getValue: ({ value }) => {
          // Converter "dinheiro" para float
          if (isMoney) {
            return Number(TextHelper.toMoney(value, '').replace(',', '.'));
          }

          return mask ? TextHelper.unmask(value) : value;
        },
        setValue: (ref, value) => {
          // Quando for "dinheiro", será necessário transformar o valor
          // para 2 casas decimais para que a máscara funcione corretamente
          if (isMoney) {
            value = TextHelper.toMoney(value, '');
          }

          setManipulated(getFormattedValue(value));
        },
        clearValue: () => {
          setManipulated('');
        },
      });
    }
  }, [loading, mask, isMoney, fieldName, registerField, getFormattedValue]);

  if (loading) {
    return <Skeleton className={classes.skeleton} variant="rect" animation="wave" />;
  }

  return (
    <TextField
      inputRef={inputRef}
      fullWidth
      id={name}
      type={type}
      name={name}
      variant="outlined"
      {...rest}
      select={select}
      value={manipulated}
      error={Boolean(error)}
      onChange={handleChange}
      inputProps={inputProps}
      InputLabelProps={InputLabelProps}
    >
      {options?.map((option) => (
        <MenuItem key={option.value} value={`${option.value}`}>
          <span>{option.label}</span>
        </MenuItem>
      ))}
    </TextField>
  );
}
